import type {
  ClientMetadata,
  IMessage,
  MessageType,
  TopicName,
} from 'magpie-types';
import { MAGPIE_CLIENT_LOGGER_NAME } from '../config/constants';
import { MetricsReporter } from '../config/metrics';
import { ConnectionStateManager } from '../connection/connection-status';
import type { MessagingProxy } from '../connection/messaging-proxy';
import { ConnectionError, ValidationSubscriber } from '../errors';
import { Logger } from 'simple-logging-lib';
import type { ElementOf, Exact, Unsubscribe } from '../types';
import { delay, getDeferred, safeCallback } from '../utils';
import { AudienceFilter, type IAudienceFilter } from './audience-filter';
import type {
  GetReplySpec,
  Message,
  IMessageDefinition,
  MessageDefinitions,
  MessageOf,
  MessageSpec,
} from './message';
import { MessageBuilder } from './message-builder';

export interface TopicSpec {
  name: TopicName;
  messages: MessageDefinitions;
  enableRuntimeValidation: boolean;
  /**
   * @deprecated Please use message replies instead
   */
  enableDeliveryConfirmation?: boolean;
  audience: IAudienceFilter;
}

export interface ITopicDefinition {
  spec: TopicSpec;
}

export interface TopicDefinition<TSpec extends TopicSpec>
  extends ITopicDefinition {
  spec: TSpec;
  getName: () => TopicName;
  hasDeliveryConfirmations: () => boolean;
  hasRuntimeValidations: () => boolean;
  getAudience: () => IAudienceFilter;
}

export type GetTopicMessageSpec<TSpec extends TopicSpec> = ElementOf<
  TSpec['messages']
>['spec'];

export type GetTopicMessageType<TSpec extends TopicSpec> = Message<
  GetTopicMessageSpec<TSpec>
>;

export type GetTopicMessageDefs<TopicDef extends ITopicDefinition> =
  TopicDef['spec']['messages'][number];

export type GetTopicMessageId<TSpec extends TopicSpec> = ElementOf<
  TSpec['messages']
>['spec']['type'];

export function defineTopic<const Name extends string, TSpec extends TopicSpec>(
  topicSpec: Exact<TSpec & { name: Name }, TopicSpec>
): TopicDefinition<TSpec> {
  return {
    spec: topicSpec,
    getName: () => topicSpec.name,
    hasDeliveryConfirmations: () => !!topicSpec.enableDeliveryConfirmation,
    hasRuntimeValidations: () => !!topicSpec.enableRuntimeValidation,
    getAudience: () => topicSpec.audience,
  };
}

type GetMessageDefByType<
  TopicDef extends ITopicDefinition,
  Type extends MessageType,
> = Extract<TopicDef['spec']['messages'][number], { spec: { type: Type } }>;

type GetMessageSpecByType<
  TopicDef extends ITopicDefinition,
  Type extends MessageType,
> = GetMessageDefByType<TopicDef, Type>['spec'];

type GetMessageByType<
  TopicDef extends ITopicDefinition,
  Type extends MessageType,
> = Message<GetMessageSpecByType<TopicDef, Type>>;

type GetTopicHasValidations<TopicDef extends ITopicDefinition> =
  TopicDef['spec']['enableRuntimeValidation'];

type WithValidations<Enabled extends boolean> = Enabled extends true
  ? {
      onValidationError: ValidationSubscriber;
    }
  : {};

type WithReply<MDef extends IMessageDefinition> =
  MDef['spec']['reply'] extends undefined
    ? {}
    : {
        reply: (
          message: MessageBuilder<Message<GetReplySpec<MDef['spec']>>>
        ) => void;
      };

export type SubscriberApi<MDef extends IMessageDefinition> =
  WithReply<MDef> & {};

type TopicMessageSubscriber<
  TopicDef extends ITopicDefinition,
  MDef extends GetTopicMessageDefs<TopicDef>,
> = (
  message: MessageOf<MDef>,
  api: SubscriberApi<MDef>
) => void | Promise<void>;

type ReplyCallback<MSpec extends MessageSpec> = (reply: Message<MSpec>) => void;

type WithReplyHandler<MSpec extends MessageSpec> =
  MSpec['reply'] extends NonNullable<MessageSpec['reply']>
    ? { onReply: ReplyCallback<GetReplySpec<MSpec>> }
    : { onReply?: never };

type SubscriptionChangeListener = (next: boolean) => void;

type IsEmptyOrAllOptional<T> = {} extends T ? true : false;

type AnyErrorHandler = (error: any) => void;

export type SubscriptionFilter<MDef extends IMessageDefinition> = (
  message: MessageOf<MDef>,
  context: ClientMetadata
) => boolean;

interface SubscriptionOptions<
  TopicDef extends ITopicDefinition,
  Type extends MessageType,
> {
  filter?: SubscriptionFilter<GetMessageDefByType<TopicDef, Type>>;
  onError?: AnyErrorHandler;
}

type PublishOptions<
  MSpec extends MessageSpec,
  TopicDef extends TopicDefinition<any>,
> = {
  onError?: AnyErrorHandler;
} & WithReplyHandler<MSpec> &
  WithValidations<GetTopicHasValidations<TopicDef>>;

export class Topic<const TopicDef extends TopicDefinition<any>> {
  private subscriptionListeners: Set<SubscriptionChangeListener> = new Set();
  protected readonly logger = Logger.getLogger(MAGPIE_CLIENT_LOGGER_NAME);

  constructor(
    private readonly definition: TopicDef,
    private readonly proxy: Promise<MessagingProxy>,
    private readonly context: ClientMetadata
  ) {}

  public subscribe = async (): Promise<void> => {
    const proxy = await this.proxy;
    this.notifySubscriptionChange(true);
    await proxy.topicSubscribe(this.definition.getName());
  };

  public unsubscribe = async (): Promise<void> => {
    const proxy = await this.proxy;
    await proxy.topicUnsubscribe(this.definition.getName());
    this.notifySubscriptionChange(false);
  };

  public publish = <MSpec extends GetTopicMessageSpec<TopicDef['spec']>>(
    mBuilder: MessageBuilder<Message<MSpec>>,
    ...args: IsEmptyOrAllOptional<PublishOptions<MSpec, TopicDef>> extends true
      ? [options?: PublishOptions<MSpec, TopicDef>]
      : [options: PublishOptions<MSpec, TopicDef>]
  ): void => {
    const [options] = args;
    const audienceFilter = this.definition.getAudience();
    mBuilder
      .setTopic(this.definition.getName())
      .setCrossPortal(audienceFilter.allowCrossPortal)
      .setAllowCrossTab(audienceFilter.allowCrossTab);

    const errorHandler: AnyErrorHandler = (error) => {
      if (options && options.onError) {
        options.onError(error);
      }
    };

    this.registerReplyCallback(mBuilder, options?.onReply).catch(errorHandler);

    if (
      this.definition.hasRuntimeValidations() &&
      options &&
      'onValidationError' in options
    ) {
      const onValidationError = safeCallback((e) => {
        options.onValidationError(e);
        MetricsReporter.reportValidationError({
          ...this.context,
          topicName: this.definition.getName(),
          messageType: mBuilder.getType(),
        });
      });
      mBuilder.validate(onValidationError);
    }

    this.useProxy()
      .then((proxy) => proxy.sendMessage(mBuilder))
      .then(() => {
        MetricsReporter.reportMessageSent({
          ...this.context,
          topicName: this.definition.getName(),
          messageType: mBuilder.getType(),
        });
      })
      .catch(errorHandler);
  };

  private isMessageTargetAudience = <
    Type extends GetTopicMessageId<TopicDef['spec']>,
  >(
    message: GetMessageByType<TopicDef, Type>,
    options: SubscriptionOptions<TopicDef, Type> & { proxy: MessagingProxy }
  ): boolean => {
    const topicAudience = AudienceFilter.from(this.definition.getAudience());
    const isTargetTopicAudience = topicAudience.isTargetAudience({
      client: this.context,
      message,
      subscriberClientId: options.proxy.clientId,
      subscriberBrokerId: options.proxy.brokerId,
    });
    const isTargetSubscriptionAudience = options.filter
      ? options.filter(message, this.context)
      : true;
    return isTargetTopicAudience && isTargetSubscriptionAudience;
  };

  /**
   * Handles the asynchronous subscription work using the proxy.
   * Obtains a proxy, constructs the message handler and registers it.
   * Adds the resulting unsubscribe function to the provided unsubscribes set.
   */
  private async subscribeWithProxy<
    Type extends GetTopicMessageId<TopicDef['spec']>,
  >(
    messageType: Type,
    safeSubscriber: TopicMessageSubscriber<TopicDef, Type>,
    options: SubscriptionOptions<TopicDef, Type> | undefined,
    isCancelled: () => boolean
  ): Promise<Unsubscribe> {
    const proxy = await this.useProxy();
    // If cancellation was already requested, skip registering a subscription.
    if (isCancelled()) {
      return () => {
        this.logger.debug(
          'Subscription already cancelled - skipping registration'
        );
      };
    }
    type ThisMessage = GetMessageByType<TopicDef, Type>;
    const handleMessage = (message: IMessage): void => {
      const shouldReject = !this.isMessageTargetAudience(
        message as ThisMessage,
        { ...options, proxy }
      );
      if (shouldReject) {
        return;
      }
      MetricsReporter.reportMessageLatency(
        {
          ...this.context,
          topicName: this.definition.getName(),
          messageType: message.type,
        },
        Date.now() - message.dispatchedAt
      );
      this.logger.debug('Received message', message);
      const api = {} as SubscriberApi<GetTopicMessageSpec<TopicDef['spec']>>;
      if (message.replyType) {
        const reply = (replyMsg: MessageBuilder<IMessage>) => {
          replyMsg.setReplyTo(message.id);
          this.publish<any>(replyMsg, {
            onError: options?.onError,
            onConfirmationReceived: undefined,
            onValidationError: undefined,
            onReply: undefined,
          });
        };
        Object.assign(api, { reply });
      }
      void safeSubscriber(message as ThisMessage, api);
    };
    return proxy.onMessage(
      messageType,
      this.definition.getName(),
      handleMessage
    );
  }

  public on<const Type extends GetTopicMessageId<TopicDef['spec']>>(
    messageType: Type,
    subscriber: TopicMessageSubscriber<
      TopicDef,
      GetMessageDefByType<TopicDef, Type>
    >,
    options?: SubscriptionOptions<TopicDef, Type>
  ): Unsubscribe {
    const unsubscribes = new Set<Unsubscribe>();
    const safeSubscriber = safeCallback(subscriber);
    let cancelled = false;
    const isCancelled = () => cancelled;

    // Execute async subscription work via the helper,
    // passing a cancellation callback.
    this.subscribeWithProxy(messageType, safeSubscriber, options, isCancelled)
      .then((unsubscribe) => {
        if (isCancelled()) {
          // If cancelled during async subscription, immediately unsubscribe.
          unsubscribe();
        } else {
          unsubscribes.add(unsubscribe);
        }
      })
      .catch((error) => {
        if (options?.onError) {
          this.logger.error(
            'Failed to subscribe to messages on topic',
            this.definition.getName(),
            error
          );
          options.onError(error);
        }
      });

    return () => {
      cancelled = true;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }

  /**
   * Subscribes once to a particular message type.
   * Returns a Promise that resolves with the first matching message,
   * which respects the topic's audience and subscription-level filters.
   * If an error occurs during subscription, the Promise is rejected.
   */
  public once<const Type extends GetTopicMessageId<TopicDef['spec']>>(
    messageType: Type,
    options?: SubscriptionOptions<TopicDef, Type>
  ): Promise<GetMessageByType<TopicDef, Type>> {
    type ThisMessage = GetMessageByType<TopicDef, Type>;
    const deferred = getDeferred<ThisMessage>();
    let unsubscribe: Unsubscribe | null = null;
    const errorHandler: AnyErrorHandler = (error) => {
      if (unsubscribe) {
        unsubscribe();
      }
      deferred.reject(error);
    };

    try {
      unsubscribe = this.on(
        messageType,
        (message) => {
          if (unsubscribe) {
            unsubscribe();
          }
          deferred.resolve(message);
        },
        { ...options, onError: errorHandler }
      );
    } catch (error) {
      deferred.reject(error);
    }
    return deferred.promise;
  }

  public onSubscriptionChange = (
    listener: SubscriptionChangeListener
  ): Unsubscribe => {
    this.subscriptionListeners.add(listener);
    return () => {
      this.subscriptionListeners.delete(listener);
    };
  };

  public removeSubscriptionChangeListener = (
    listener: SubscriptionChangeListener
  ) => {
    this.subscriptionListeners.delete(listener);
  };

  private notifySubscriptionChange = (next: boolean) => {
    this.subscriptionListeners.forEach((listener) => {
      try {
        return listener(next);
      } catch (e) {
        this.logger.error(e);
        // Re-throw but make sure other listeners are still called
        setTimeout(() => {
          throw e;
        }, 0);
      }
    });
  };

  private useProxy = async (): Promise<MessagingProxy> => {
    const proxy = await Promise.race([
      this.proxy,
      delay(5000).then(() => null),
    ]);
    if (proxy === null) {
      const connectionState = ConnectionStateManager.getInstance();
      throw new ConnectionError(ConnectionError.Reason.UNINITIALIZED, {
        messageArgs: [connectionState.getStatus()],
      });
    }
    return proxy;
  };

  private registerReplyCallback = <MSpec extends MessageSpec>(
    mBuilder: MessageBuilder<Message<MSpec>>,
    onReplyCallback?: ReplyCallback<GetReplySpec<MSpec>>
  ): Promise<void> => {
    if (!onReplyCallback) {
      return Promise.resolve();
    }
    const replyType = mBuilder.getReplyType();
    if (!replyType) {
      throw new Error('Message does not have a reply type');
    }
    const { resolve, promise } = getDeferred<Message<MSpec>>();
    const unsubscribe = this.on(replyType, (maybeReplyMessage) => {
      if (
        maybeReplyMessage.type !== replyType ||
        maybeReplyMessage.replyTo !== mBuilder.getId()
      ) {
        return;
      }
      const safeSubscriber = safeCallback(onReplyCallback);
      safeSubscriber(maybeReplyMessage);
      resolve(maybeReplyMessage);
    });
    return promise.then(() => unsubscribe());
  };
}

export type TopicMessages<T extends TopicDefinition<any>> =
  T extends TopicDefinition<infer TSpec> ? GetTopicMessageType<TSpec> : never;
